/**
 * React Query hooks for Project CRUD operations.
 * Uses tRPC client for type-safe API calls.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import type { Project } from "../types";

// List projects, optionally filtered by serialId or groupId
export function useProjects(filters?: { serialId?: string; groupId?: string }) {
  return useQuery({
    queryKey: ["projects", "list", filters],
    queryFn: () => trpc.project.list.query(filters || {}),
  }) as { data: Project[]; isLoading: boolean; isError: boolean; error: any };
}

// Get a single project by ID
export function useProject(projectId: string) {
  return useQuery({
    queryKey: ["projects", "get", projectId],
    queryFn: () => trpc.project.get.query(projectId),
    enabled: !!projectId,
  }) as { data: Project | undefined; isLoading: boolean; isError: boolean; error: any };
}

// Create a new project
export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      key: string;
      title: string;
      projectType: string;
      description?: string;
      serialId?: string;
      groupId?: string;
      status?: string;
      startAt?: string;
      dueAt?: string;
    }) => {
      return await trpc.project.create.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// Update an existing project
export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<Project> & {
      stages?: Record<string, boolean>;
      stageAssignees?: Record<string, string | null>;
    }) => {
      return await trpc.project.update.mutate(input as any);
    },
    onSuccess: async (_data, variables) => {
      // Invalidate and refetch to ensure UI is in sync
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// Delete a project
export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await trpc.project.delete.mutate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}